import { Link } from 'react-router-dom';
import {
  REPORT_TYPES,
  needsScan,
  type EventAnalysisDef,
  type ReportListItem,
  type ReportSection,
} from '@ni/shared';
import { useReport } from '../../api/report-hooks';
import {
  EVENT_ANALYSIS_FULL_REPORT,
  EVENT_REPORT_FROM,
  eventReportAdds,
  eventReportVersion,
} from '../../lib/copy';
import { formatDay } from '../../lib/format';

/** How many of a report-only analysis's sections the tab quotes. */
const REPORT_ONLY_QUOTED = 2;
/**
 * A rated analysis's Summary opens with the event in one sentence, then the
 * counts the ring already shows (the readout writer's prescribed order,
 * `agent/app/core/prompts/report_writer_crowd_summary.md` and its siblings);
 * what follows is what the scan cannot say.
 */
const SUMMARY_SAID_BY_SCAN = 2;

export interface Excerpt {
  quoted: { id: string; title: string; passages: { id: string; text: string }[] }[];
  /** Titles of the sections the tab does not quote, in report order. */
  rest: string[];
  /** The report writes up each of the scan's groups. */
  groups: boolean;
}

/**
 * **What the tab quotes from the latest version** (the blend spec's "Analysis
 * summaries"): only what the scan doesn't already say. A rated analysis quotes
 * its Summary past the event and the counts; its group write-ups repeat the
 * scan shown above. A report-only analysis quotes its first sections whole.
 * A passage citing a sensitive fact stays in the report.
 */
export function excerptOf(analysis: EventAnalysisDef, sections: readonly ReportSection[]): Excerpt {
  const type = REPORT_TYPES[analysis.report];
  const perPerson = new Set(type.default_sections.filter((s) => s.per_person).map((s) => s.key));
  const said = (section: ReportSection) =>
    section.passages.filter((passage) => !passage.sensitive).map(({ id, text }) => ({ id, text }));

  if (!needsScan(analysis)) {
    const quotable = sections.filter((s) => !(s.key && perPerson.has(s.key)));
    const quoted = quotable
      .map((s) => ({ id: s.id, title: s.title, passages: said(s) }))
      .filter((s) => s.passages.length > 0)
      .slice(0, REPORT_ONLY_QUOTED);
    const ids = new Set(quoted.map((s) => s.id));
    return {
      quoted,
      rest: sections.filter((s) => !ids.has(s.id)).map((s) => s.title),
      groups: false,
    };
  }

  /* The readout's own keys beside the summary and gaps are its groups' write-ups. */
  const groupKeys = new Set(
    type.default_sections.map((s) => s.key).filter((key) => key !== 'summary' && key !== 'gaps'),
  );
  const summary = sections.find((s) => s.key === 'summary');
  const beyond = summary
    ? said({ ...summary, passages: summary.passages.slice(SUMMARY_SAID_BY_SCAN) })
    : [];
  const quoted =
    summary && beyond.length > 0
      ? [{ id: summary.id, title: summary.title, passages: beyond }]
      : [];
  return {
    quoted,
    rest: sections
      .filter(
        (s) => !(s.key && groupKeys.has(s.key)) && !(s.key === 'summary' && quoted.length > 0),
      )
      .map((s) => s.title),
    groups: sections.some((s) => s.key !== null && groupKeys.has(s.key)),
  };
}

/**
 * **The report's top sections, quoted on the tab**, then "Read the full report"
 * naming what it adds. Quoted text sits on the panel's one left edge: no
 * hanging numbers, no second indent.
 */
export function ReportExcerpt({
  analysis,
  item,
}: {
  analysis: EventAnalysisDef;
  item: ReportListItem;
}) {
  const report = useReport(item.id);
  const version = report.data?.current_version ?? null;
  /* The link stands while the version is read, or when it cannot be. */
  if (!version)
    return (
      <div className="an-report bare">
        <p className="an-report-more">
          <Link className="link" to={`/reports/${item.id}`}>
            {EVENT_ANALYSIS_FULL_REPORT}
          </Link>
        </p>
      </div>
    );
  const excerpt = excerptOf(analysis, version.sections);
  const stamp = eventReportVersion(version.number, formatDay(version.created_at));
  return (
    <div className={excerpt.quoted.length > 0 ? 'an-report' : 'an-report bare'}>
      {excerpt.quoted.length > 0 ? (
        <p className="an-report-h">
          <span className="sc">{EVENT_REPORT_FROM}</span>
          <span className="an-report-v">{stamp}</span>
        </p>
      ) : null}
      {excerpt.quoted.map((section) => (
        <section className="an-x" key={section.id}>
          <h3>{section.title}</h3>
          {section.passages.map((passage) => (
            <p key={passage.id}>{passage.text}</p>
          ))}
        </section>
      ))}
      <p className="an-report-more">
        <Link className="link" to={`/reports/${item.id}`}>
          {EVENT_ANALYSIS_FULL_REPORT}
        </Link>
        <span>
          {excerpt.quoted.length > 0 ? '' : `${stamp}. `}
          {eventReportAdds(excerpt.rest, excerpt.groups)}
        </span>
      </p>
    </div>
  );
}
